// Pure display-math for the Crew Pack buy box. Shopify is the pricing source
// of truth: the kit product's variant price is the one-time bundle price, its
// compare-at price is the retail value, and its Recharge selling plans set the
// per-delivery subscription price — everything here is presentation only.

import type { Money, SellingPlan } from '@repo/shopify';
import type {
	HydrateBundleFlavor,
	HydrateBundleKitProduct,
	InsiderBundleSettings,
} from '@/lib/insider-bundle-config';
import {
	DEFAULT_SELLING_PLAN_INTERVAL_DAYS,
	getSellingPlanFrequencyPhrase,
	getSellingPlanIntervalDays,
	getSellingPlanUnitPrice,
} from '@/lib/selling-plans';

/** Bundle slot contents: flavor handles, null = empty. Bonus slots come last. */
export type BundleSlots = (string | null)[];

export interface BundleFrequencyOption {
	/** Canonical key, e.g. "every month" / "every 4 months". */
	phrase: string;
	intervalDays: number;
	/** The kit product's plan for this frequency. */
	plan: SellingPlan;
}

export function createEmptySlots(config: InsiderBundleSettings): BundleSlots {
	return Array.from({ length: config.boxCount + config.bonusCount }, () => null);
}

/**
 * Clears one pick and compacts its region so later picks slide down to fill
 * the gap in order — empty slots always collect at the region's end. Paid
 * slots and bonus slots compact independently (the bonus slot never absorbs
 * a paid pick or vice versa). Returns the same array when nothing changes.
 */
export function clearSlotAndCompact(
	slots: BundleSlots,
	index: number,
	boxCount: number,
): BundleSlots {
	if (slots[index] == null) return slots;

	const regionStart = index < boxCount ? 0 : boxCount;
	const regionEnd = index < boxCount ? Math.min(boxCount, slots.length) : slots.length;
	const region = slots.slice(regionStart, regionEnd);
	region.splice(index - regionStart, 1);
	region.push(null);
	return [...slots.slice(0, regionStart), ...region, ...slots.slice(regionEnd)];
}

export function getTotalSticks(config: InsiderBundleSettings): number {
	return (config.boxCount + config.bonusCount) * config.sticksPerBox;
}

/**
 * Delivery frequencies offered for the bundle: the kit product's recurring
 * plans, keyed by frequency phrase (plan NAMES are unreliable — see
 * selling-plans.ts) and ordered shortest first.
 */
export function getBundleFrequencyOptions(
	kitProduct: HydrateBundleKitProduct | null | undefined,
): BundleFrequencyOption[] {
	const options: BundleFrequencyOption[] = [];
	for (const plan of kitProduct?.sellingPlans ?? []) {
		const phrase = getSellingPlanFrequencyPhrase(plan);
		const intervalDays = getSellingPlanIntervalDays(plan);
		if (!phrase || intervalDays === null) continue;
		if (options.some((option) => option.phrase === phrase)) continue;

		options.push({ phrase, intervalDays, plan });
	}

	return options.sort((a, b) => a.intervalDays - b.intervalDays);
}

/**
 * The pre-selected frequency: the shopper-default interval (45 days, same as
 * the single-bag buy box) when the kit offers it, else the shortest.
 */
export function getDefaultFrequencyOption(
	options: BundleFrequencyOption[],
): BundleFrequencyOption | null {
	return (
		options.find((option) => option.intervalDays === DEFAULT_SELLING_PLAN_INTERVAL_DAYS) ??
		options[0] ??
		null
	);
}

function sumMoney(amounts: number, currencyCode: string): Money {
	return { amount: (Math.round(amounts * 100) / 100).toFixed(2), currencyCode };
}

export interface BundlePricing {
	/** Full retail value of every bag bought separately (the "$180 value"). */
	retailTotal: Money;
	/** The kit product's one-time price. */
	oneTimeTotal: Money;
	/** Per-delivery price on the selected frequency's plan. */
	subscribeTotal: Money | null;
	/** Rounded percent saved subscribing vs the retail value, null when not cheaper. */
	subscribePercentOff: number | null;
}

/**
 * Display totals for the bundle. The kit product is the source of truth; the
 * retail value prefers its compare-at price and falls back to summing every
 * slot at the flavors' one-time price (empty slots price at the cheapest
 * flavor — all hydrate flavors share one price today, making this moot).
 * Savings are framed against that retail value per the Crew Pack pricing
 * sheet ($180 → $135 one-time / $120 subscribing).
 */
export function computeBundlePricing(
	kitProduct: HydrateBundleKitProduct,
	flavors: HydrateBundleFlavor[],
	slots: BundleSlots,
	frequency: BundleFrequencyOption | null,
): BundlePricing | null {
	const currencyCode = kitProduct.price.currencyCode;

	let retailTotal = kitProduct.compareAtPrice;
	if (!retailTotal || retailTotal.currencyCode !== currencyCode) {
		const byHandle = new Map(flavors.map((flavor) => [flavor.handle, flavor]));
		const fallbackFlavor = flavors.reduce<HydrateBundleFlavor | null>(
			(cheapest, candidate) =>
				!cheapest ||
				Number.parseFloat(candidate.price.amount) < Number.parseFloat(cheapest.price.amount)
					? candidate
					: cheapest,
			null,
		);
		if (!fallbackFlavor) return null;

		let retailAmount = 0;
		for (const handle of slots) {
			const flavor = (handle ? byHandle.get(handle) : null) ?? fallbackFlavor;
			retailAmount += Number.parseFloat(flavor.price.amount);
		}
		retailTotal = sumMoney(retailAmount, currencyCode);
	}

	const subscribeTotal = frequency
		? getSellingPlanUnitPrice(kitProduct.price, frequency.plan)
		: null;

	const retailAmount = Number.parseFloat(retailTotal.amount);
	const subscribePercentOff =
		subscribeTotal && retailAmount > 0
			? Math.round(((retailAmount - Number.parseFloat(subscribeTotal.amount)) / retailAmount) * 100)
			: null;

	return {
		retailTotal,
		oneTimeTotal: kitProduct.price,
		subscribeTotal,
		subscribePercentOff:
			subscribePercentOff !== null && subscribePercentOff > 0 ? subscribePercentOff : null,
	};
}
